import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../../lib/utils"

const inputVariants = cva(
  // base: border-1, white bg, secondary text, placeholder, subtle shadow, no ring offset gap
  "w-full border outline-none transition-colors bg-white text-card-foreground shadow-sm ring-offset-0 font-[var(--font-family--sans)] " +
  "placeholder:text-muted-foreground " +
  "disabled:cursor-not-allowed disabled:opacity-40 disabled:bg-muted disabled:border-dashed disabled:text-epic-neutral-9",
  {
    variants: {
      variant: {
        // default border = epic-neutral-6 (#D4DAE5 = general/border/solid-20)
        // hover  border = epic-neutral-9 (#828EA0)
        // focus  border = epic-neutral-11 (#5A6574 â‰ˆ general/border/solid-50)
        // focus  ring   = 3px, --ring color
        default:
          "border-epic-neutral-6 " +
          "hover:border-epic-neutral-9 " +
          "focus:border-epic-neutral-11 focus:ring focus:ring-ring focus:ring-offset-0",
        // error  border = #DC2626 (general/border/destructive)
        // errorFocus ring = 3px, destructive/20
        error:
          "border-destructive " +
          "hover:border-destructive " +
          "focus:border-destructive focus:ring focus:ring-destructive/20 focus:ring-offset-0",
      },
      inputType: {
        default: "",
        rounded: "rounded-full",
      },
      size: {
        large:   "h-10 px-4 text-sm",   // 40px, semantic/md padding
        regular: "h-9  px-3 text-sm",   // 36px, absolute/3 padding
        small:   "h-8  px-2 text-sm",   // 32px, semantic/xs padding
        mini:    "h-6  px-2 text-xs",   // 24px, semantic/xs padding
        // backward-compat alias â€” same values as regular
        default: "h-9  px-3 text-sm",
      },
    },
    compoundVariants: [
      // inputType=default gets rounded-lg (8px = semantic/rounded-lg) for all sizes
      { inputType: "default", size: "large",   class: "rounded-lg" },
      { inputType: "default", size: "regular", class: "rounded-lg" },
      { inputType: "default", size: "small",   class: "rounded-lg" },
      { inputType: "default", size: "mini",    class: "rounded-lg" },
      { inputType: "default", size: "default", class: "rounded-lg" },
    ],
    defaultVariants: {
      variant: "default",
      inputType: "default",
      size: "regular",
    },
  }
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  rightIcon?: React.ReactNode
  leftIcon?: React.ReactNode
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, size, inputType, rightIcon, leftIcon, ...props }, ref) => {
    const inputClass = cn(
      inputVariants({ variant, size, inputType }),
      leftIcon && "pl-8",
      rightIcon && "pr-9",
      className
    )

    if (rightIcon || leftIcon) {
      return (
        <div className="relative w-full">
          {leftIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center text-muted-foreground">
              {leftIcon}
            </span>
          )}
          <input ref={ref} className={inputClass} {...props} />
          {rightIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center text-muted-foreground cursor-pointer select-none">
              {rightIcon}
            </span>
          )}
        </div>
      )
    }

    return <input ref={ref} className={inputClass} {...props} />
  }
)
Input.displayName = "Input"

export { Input, inputVariants }
